import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { CookieCategory, ICookiePreferences } from '../../../interfaces/cookie-consent.interface';
import { CookieConsentService } from '../../../services/cookie-consent.service';

/**
 * Cookie settings modal for granular cookie consent control.
 *
 * Features:
 * - Toggle individual cookie categories
 * - Display services affected by each category
 * - Prevent disabling necessary cookies
 * - Save and cancel actions
 * - Accessible modal design
 * - Mobile-optimized layout
 */
@Component({
  selector: 'cookie-settings',
  standalone: true,
  imports: [FormsModule, TranslateModule, NzModalModule, NzSwitchModule, NzButtonModule, NzDividerModule, NzTagModule],
  templateUrl: './cookie-settings.component.html',
  styleUrls: ['./cookie-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieSettingsComponent {
  readonly #cookieConsentService = inject(CookieConsentService);

  readonly close = output<void>();

  /** Modal visibility */
  protected readonly visible = signal(true);

  /** Loading state for save action */
  protected readonly saving = signal(false);

  /** Cookie categories from service */
  protected readonly categories = this.#cookieConsentService.categories;

  /** Current preferences (local copy) */
  protected preferences = signal<ICookiePreferences>(this.#cookieConsentService.getPreferences());

  /**
   * Check if a category is required (cannot be disabled)
   */
  protected isRequired(categoryKey: CookieCategory): boolean {
    const category = this.categories.find(c => c.key === categoryKey);
    return category?.required ?? false;
  }

  /**
   * Get the preference value for a category
   */
  protected getCategoryPreference(categoryKey: CookieCategory): boolean {
    return this.preferences()[categoryKey];
  }

  /**
   * Toggle a cookie category
   */
  protected toggleCategory(categoryKey: CookieCategory, enabled: boolean): void {
    const currentPrefs = this.preferences();
    this.preferences.set({
      ...currentPrefs,
      [categoryKey]: enabled,
    });
  }

  /**
   * Save preferences and close modal
   */
  protected async onSave(): Promise<void> {
    this.saving.set(true);
    try {
      await this.#cookieConsentService.saveCustomPreferences(this.preferences());
      this.onClose();
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * Close modal without saving
   */
  protected onClose(): void {
    this.visible.set(false);
    setTimeout(() => this.close.emit(), 300); // Wait for animation
  }
}
